
import { useState } from 'react';
import useCampaign from '../../../hooks/useCampaign';

const plans = [
  {
    id: 'light',
    name: 'ライトプラン',
    nameEn: 'Light',
    price: 1620,
    period: '/月',
    description: 'まずは気軽に日本茶を楽しみたい方に',
    features: [
      '月替わり1種類（10パック入り）',
      'シングルオリジン茶葉',
      'お茶の解説カード付き',
      'いつでも解約可能',
    ],
    recommended: false,
    color: '#7B8C3E',
  },
  {
    id: 'standard',
    name: 'スタンダードプラン',
    nameEn: 'Standard',
    price: 2980,
    period: '/月',
    description: '複数の味わいを楽しみたい方におすすめ',
    features: [
      '月替わり2種類（各10パック入り）',
      'シングルオリジン茶葉',
      'お茶の解説カード付き',
      '茶師のおすすめレシピ',
      'いつでも解約可能',
    ],
    recommended: true,
    color: '#7B8C3E',
  },
  {
    id: 'premium',
    name: 'プレミアムプラン',
    nameEn: 'Premium',
    price: 4980,
    period: '/月',
    description: '本格的に日本茶を探求したい方に',
    features: [
      '月替わり3種類（各10パック入り）',
      '限定シングルオリジン茶葉',
      'お茶の解説カード付き',
      '茶師のおすすめレシピ',
      '季節の特別茶葉サンプル',
      '優先カスタマーサポート',
    ],
    recommended: false,
    color: '#7B8C3E',
  },
];

function formatPrice(price: number): string {
  return `¥${price.toLocaleString()}`;
}

export default function TeaPlans() {
  const [hoveredPlan, setHoveredPlan] = useState<string | null>(null);
  const { isActive, discountRate } = useCampaign();

  const getDiscountedPrice = (price: number) => {
    return Math.round(price * (1 - discountRate / 100));
  };

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#FAF8F5]" id="plans" aria-labelledby="tea-plans-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <span className="inline-block px-3 py-1.5 bg-[#7B8C3E]/10 text-[#7B8C3E] text-xs tracking-[0.15em] rounded-full mb-4 sm:mb-6">
            SUBSCRIPTION PLANS
          </span>
          <h2 id="tea-plans-heading" className="text-3xl sm:text-4xl md:text-5xl font-extralight text-black mb-3 sm:mb-4 tracking-[0.1em]">
            定期便プラン
          </h2>
          <p className="text-sm sm:text-base text-black/60 font-extralight tracking-wide">
            あなたに合ったプランをお選びください
          </p>
          {isActive && (
            <div className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-medium rounded-full shadow-lg">
              <i className="ri-fire-fill" aria-hidden="true"></i>
              <span>初回限定30%OFF キャンペーン適用中</span>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto" role="list">
          {plans.map((plan) => (
            <article
              key={plan.id}
              className={`relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 transition-all duration-300 cursor-pointer ${
                plan.recommended
                  ? 'ring-2 ring-[#7B8C3E] shadow-xl shadow-[#7B8C3E]/10'
                  : hoveredPlan === plan.id
                  ? 'shadow-xl -translate-y-1'
                  : 'shadow-sm hover:shadow-lg'
              }`}
              onMouseEnter={() => setHoveredPlan(plan.id)}
              onMouseLeave={() => setHoveredPlan(null)}
              role="listitem"
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1.5 bg-[#7B8C3E] text-white text-xs font-medium tracking-[0.1em] rounded-full whitespace-nowrap">
                    おすすめ
                  </span>
                </div>
              )}

              {isActive && (
                <div className="absolute -top-3 right-4">
                  <span className="px-3 py-1 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] font-medium tracking-wide rounded-full whitespace-nowrap shadow-md">
                    初回30%OFF
                  </span>
                </div>
              )}

              <div className="text-center mb-6 sm:mb-8">
                <span className="text-xs text-black/40 font-light tracking-[0.2em] uppercase">{plan.nameEn}</span>
                <h3 className="text-xl sm:text-2xl font-light text-black mt-1 mb-2 tracking-wide">{plan.name}</h3>
                <p className="text-sm text-black/60 font-extralight">{plan.description}</p>
              </div>

              <div className="text-center mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-gray-100">
                {isActive ? (
                  <>
                    <div className="text-sm text-black/40 line-through mb-1">{formatPrice(plan.price)}</div>
                    <div className="flex items-baseline justify-center">
                      <span className="text-3xl sm:text-4xl font-light text-orange-500">{formatPrice(getDiscountedPrice(plan.price))}</span>
                      <span className="text-sm text-black/60 ml-1">{plan.period}</span>
                    </div>
                    <span className="text-xs text-orange-500/80 mt-1 block font-medium">初回配送分 30%OFF</span>
                    <span className="text-[10px] text-black/40 mt-0.5 block">（税込・送料無料）</span>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline justify-center">
                      <span className="text-3xl sm:text-4xl font-light text-black">{formatPrice(plan.price)}</span>
                      <span className="text-sm text-black/60 ml-1">{plan.period}</span>
                    </div>
                    <span className="text-xs text-black/40 mt-1 block">（税込・送料無料）</span>
                  </>
                )}
              </div>

              <ul className="space-y-3 sm:space-y-4 mb-8" aria-label={`${plan.name}の内容`}>
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#7B8C3E]" aria-hidden="true"></i>
                    </div>
                    <span className="text-sm text-black/70 font-extralight">{feature}</span>
                  </li>
                ))}
              </ul>

              <a
                href="https://crafttea.shop/"
                target="_blank"
                rel="noopener noreferrer"
                className={`block w-full py-3 sm:py-4 text-center text-sm font-light tracking-[0.1em] rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600 font-medium shadow-md'
                    : plan.recommended
                    ? 'bg-[#7B8C3E] text-white hover:bg-[#6A7A35]'
                    : 'bg-black text-white hover:bg-black/80'
                }`}
                aria-label={`${plan.name}に申し込む（Craft Tea外部サイト）`}
              >
                {isActive && <i className="ri-fire-fill mr-1.5" aria-hidden="true"></i>}
                {isActive ? 'キャンペーン価格で申し込む' : '申し込む'}
              </a>
            </article>
          ))}
        </div>

        <p className="text-center text-xs text-black/40 font-extralight mt-8 sm:mt-10 tracking-wide">
          ※ サブスクリプションはCraft Teaのサイトからお申し込みいただけます
          {isActive && <><br />※ キャンペーン価格は初回配送分のみ適用されます。2回目以降は通常価格となります。</>}
        </p>
      </div>
    </section>
  );
}
